const PKCE_VERIFIER_KEY = 'amandamd_auth_pkce_verifier_v1';
const OAUTH_STATE_KEY = 'amandamd_auth_state_v1';
const POST_LOGIN_PATH_KEY = 'amandamd_auth_post_login_path_v1';

function getEnvValue(name) {
  if (
    typeof import.meta !== 'undefined' &&
    import.meta &&
    import.meta.env &&
    typeof import.meta.env[name] === 'string'
  ) {
    return import.meta.env[name];
  }
  return '';
}

function normalizeDomain(rawDomain) {
  if (!rawDomain) {
    return '';
  }

  const trimmed = rawDomain.trim().replace(/\/+$/, '');
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

function base64UrlEncode(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function randomBytes(length) {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function randomString(length) {
  return base64UrlEncode(randomBytes(length)).slice(0, length);
}

async function sha256(input) {
  const encoded = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded);
  return new Uint8Array(digest);
}

function parseJwtPayload(token) {
  if (!token || token.split('.').length < 2) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function requireBrowserStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    throw new Error('Browser session storage is required for Cognito auth.');
  }
}

export function getCognitoConfig() {
  const domain = normalizeDomain(getEnvValue('VITE_COGNITO_DOMAIN'));
  const clientId = getEnvValue('VITE_COGNITO_CLIENT_ID').trim();
  const redirectUri = getEnvValue('VITE_COGNITO_REDIRECT_URI').trim()
    || `${window.location.origin}/auth/callback`;
  const logoutUri = getEnvValue('VITE_COGNITO_LOGOUT_URI').trim()
    || `${window.location.origin}/login`;
  const scope = getEnvValue('VITE_COGNITO_SCOPE').trim() || 'openid email profile';

  return {
    domain,
    clientId,
    redirectUri,
    logoutUri,
    scope,
  };
}

export function isCognitoConfigured(config = getCognitoConfig()) {
  return Boolean(config.domain && config.clientId && config.redirectUri);
}

function buildAuthorizeUrl(config, state, codeChallenge) {
  const url = new URL(`${config.domain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('scope', config.scope);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('code_challenge', codeChallenge);
  return url.toString();
}

export async function beginCognitoSignIn(config, postLoginPath = '/') {
  requireBrowserStorage();

  const verifier = randomString(96);
  const state = randomString(48);
  const challenge = base64UrlEncode(await sha256(verifier));
  const authorizeUrl = buildAuthorizeUrl(config, state, challenge);

  window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
  window.sessionStorage.setItem(POST_LOGIN_PATH_KEY, postLoginPath || '/');

  window.location.assign(authorizeUrl);
}

function buildTokenRequestBody(config, code, verifier) {
  const params = new URLSearchParams();
  params.set('grant_type', 'authorization_code');
  params.set('client_id', config.clientId);
  params.set('code', code);
  params.set('redirect_uri', config.redirectUri);
  params.set('code_verifier', verifier);
  return params;
}

function normalizeTokenResponse(data) {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const idClaims = parseJwtPayload(data.id_token);
  const accessClaims = parseJwtPayload(data.access_token);
  const expiresAt = accessClaims?.exp || (nowSeconds + Number(data.expires_in || 3600));

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token || null,
    tokenType: data.token_type || 'Bearer',
    expiresAt,
    issuedAt: nowSeconds,
    email: idClaims?.email || null,
    subject: idClaims?.sub || null,
  };
}

export async function completeCognitoSignIn(config, code, returnedState) {
  requireBrowserStorage();

  const expectedState = window.sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const postLoginPath = window.sessionStorage.getItem(POST_LOGIN_PATH_KEY) || '/';

  window.sessionStorage.removeItem(OAUTH_STATE_KEY);
  window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);

  if (!expectedState || !verifier) {
    throw new Error('Missing authentication state. Start sign-in again.');
  }
  if (!returnedState || returnedState !== expectedState) {
    throw new Error('State validation failed. Start sign-in again.');
  }

  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: buildTokenRequestBody(config, code, verifier),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof data.error_description === 'string'
      ? data.error_description
      : 'Unable to complete sign-in.';
    throw new Error(message);
  }

  return {
    tokens: normalizeTokenResponse(data),
    postLoginPath,
  };
}

export function buildCognitoLogoutUrl(config) {
  const url = new URL(`${config.domain}/logout`);
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('logout_uri', config.logoutUri);
  return url.toString();
}
